import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileText, FileSignature, Stamp, Map as MapIcon, File as FileIcon,
  Upload, Download, Trash2, Paperclip, Loader2, X,
} from "lucide-react";

const DOC_TYPES = [
  { key: "contrato", label: "Contrato", icon: FileSignature },
  { key: "licencia", label: "Licencia", icon: Stamp },
  { key: "plano", label: "Plano", icon: MapIcon },
  { key: "otro", label: "Otro", icon: FileIcon },
];
const TYPE_BY_KEY = Object.fromEntries(DOC_TYPES.map((t) => [t.key, t]));
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.dwg";
const MAX_BYTES = 25 * 1024 * 1024;

function formatBytes(n) {
  if (!n) return "0 KB";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
}

export default function PhaseDocuments({ projectId, phaseId, canDelete = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("otro");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/documents`, { params: { phase_id: phaseId } });
      setDocs(data);
    } catch {
      toast.error("No se pudieron cargar los documentos.");
    } finally {
      setLoading(false);
    }
  }, [projectId, phaseId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setFile(null); setDocType("otro"); setDescription(""); if (fileRef.current) fileRef.current.value = ""; };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error("El archivo supera el tamaño máximo (25 MB)."); e.target.value = ""; return; }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return toast.error("Selecciona un archivo.");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("description", description.trim());
    try {
      const { data } = await api.post(`/projects/${projectId}/phases/${phaseId}/documents`, fd);
      setDocs((d) => [data, ...d]);
      toast.success("Documento subido.");
      setOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el documento.");
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/projects/${projectId}/documents/${doc.document_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el documento.");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`¿Eliminar «${doc.original_filename}»? Esta acción no se puede deshacer.`)) return;
    setDeletingId(doc.document_id);
    const prev = docs;
    setDocs((d) => d.filter((x) => x.document_id !== doc.document_id));
    try {
      await api.delete(`/projects/${projectId}/documents/${doc.document_id}`);
      toast.success("Documento eliminado.");
    } catch {
      toast.error("No se pudo eliminar el documento.");
      setDocs(prev);
    } finally {
      setDeletingId(null);
    }
  };

  const visible = filter === "all" ? docs : docs.filter((d) => d.doc_type === filter);
  const countByType = (k) => docs.filter((d) => d.doc_type === k).length;

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card" data-testid="phase-documents">
      <div className="flex items-center justify-between gap-3 p-6 sm:p-7 border-b border-border/70">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="shrink-0 h-10 w-10 rounded-lg bg-[#C75B28]/10 text-[#C75B28] flex items-center justify-center">
            <Paperclip className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold tracking-tight leading-tight">Documentos de la fase</h3>
            <p className="text-sm text-muted-foreground leading-tight mt-0.5">Contratos, licencias y planos de esta fase.</p>
          </div>
        </div>
        <Button className="rounded-full gap-1.5 shrink-0" onClick={() => setOpen(true)} data-testid="upload-document-button">
          <Upload className="h-4 w-4" /> Subir documento
        </Button>
      </div>

      <div className="p-6 sm:p-7">
        <div className="flex flex-wrap items-center gap-1 mb-4">
          <button
            onClick={() => setFilter("all")}
            data-testid="doc-filter-all"
            className={`text-sm px-3 py-1.5 rounded-full transition-colors ${filter === "all" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-background-subtle"}`}
          >
            Todos ({docs.length})
          </button>
          {DOC_TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              data-testid={`doc-filter-${t.key}`}
              className={`text-sm px-3 py-1.5 rounded-full transition-colors ${filter === t.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-background-subtle"}`}
            >
              {t.label} ({countByType(t.key)})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-muted-foreground" data-testid="docs-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-background/60 py-10 text-center" data-testid="docs-empty">
            <Paperclip className="h-6 w-6 mx-auto text-muted-foreground/60" />
            <p className="text-sm text-muted-foreground mt-2">Aún no hay documentos en esta fase.</p>
            <p className="text-xs text-muted-foreground mt-1">Pulsa «Subir documento» para añadir contratos, licencias o planos.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visible.map((doc, i) => {
              const t = TYPE_BY_KEY[doc.doc_type] || TYPE_BY_KEY.otro;
              const Icon = t.icon;
              return (
                <div key={doc.document_id} data-testid={`document-row-${i}`}
                  className="group flex items-start gap-3 rounded-xl border border-border bg-background/60 p-3 hover:bg-background-subtle transition-colors">
                  <div className="shrink-0 h-10 w-10 rounded-lg bg-secondary text-foreground/70 flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate max-w-[16rem]" title={doc.original_filename}>{doc.original_filename}</span>
                      <Badge variant="outline" className="font-normal text-[10px] shrink-0">{t.label}</Badge>
                    </div>
                    {doc.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{doc.description}</p>}
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {formatBytes(doc.size)} · {doc.uploaded_by_name || "—"} · {formatDate(doc.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => download(doc)} data-testid={`download-document-${i}`} aria-label="Descargar">
                      <Download className="h-4 w-4" />
                    </Button>
                    {canDelete && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => remove(doc)} disabled={deletingId === doc.document_id}
                        data-testid={`delete-document-${i}`} aria-label="Eliminar">
                        {deletingId === doc.document_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!uploading) { setOpen(v); if (!v) resetForm(); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="upload-document-modal">
          <DialogHeader>
            <DialogTitle>Subir documento</DialogTitle>
            <DialogDescription>PDF, imágenes (JPG/PNG), Word, Excel o planos (DWG). Máximo 25 MB.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Archivo *</Label>
              <input ref={fileRef} type="file" accept={ACCEPT} onChange={onPickFile} className="hidden" data-testid="document-file-input" />
              {file ? (
                <div className="flex items-center gap-2 rounded-xl border border-border bg-background-subtle p-3" data-testid="document-file-chosen">
                  <FileText className="h-4 w-4 text-[#C75B28] shrink-0" />
                  <span className="text-sm truncate flex-1">{file.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{formatBytes(file.size)}</span>
                  <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-muted-foreground hover:text-destructive" aria-label="Quitar archivo">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Button type="button" variant="outline" className="w-full rounded-xl gap-2 justify-center" onClick={() => fileRef.current?.click()} data-testid="document-pick-file-button">
                  <Upload className="h-4 w-4" /> Seleccionar archivo
                </Button>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de documento</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="document-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t.key} value={t.key} data-testid={`document-type-option-${t.key}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-desc">Descripción / nota</Label>
              <Textarea id="doc-desc" rows={2} placeholder="Opcional" value={description}
                onChange={(e) => setDescription(e.target.value)} data-testid="document-description-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => { setOpen(false); resetForm(); }} disabled={uploading}>Cancelar</Button>
            <Button type="button" className="rounded-full gap-1.5" onClick={submit} disabled={uploading || !file} data-testid="document-submit-button">
              {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Subiendo…</> : <><Upload className="h-4 w-4" /> Subir</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
